/* eslint-disable import/prefer-default-export */
import {
    PropertyByPermission,
    PropertyByPermissionAPI,
    UserPermissions,
    OccupantByPermission,
} from '@centecomm/portal-service';
import { useProperty } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUniqueItems } from 'utils/getUniqueItems';
import { PropertyTenantResolution } from '../../State';
import { LoadStatus } from '../../Types';

const { Actions, selectors } = PropertyTenantResolution;

type PropertyTenantResolutionHook = (
    permission: UserPermissions,
    disablePropertyFetch?: boolean,
    getAssociatedPropertiesOnly?: boolean,
) => [boolean, (propertyId: number) => void, boolean];

export const usePropertyTenantResolution: PropertyTenantResolutionHook = (
    permission,
    disablePropertyFetch = false,
    getAssociatedPropertiesOnly,
) => {
    const dispatch = useDispatch();
    const { getPropertiesByPermission, getOccupantsByPermission, getAssociatedPropertiesByPermission } = useProperty();

    const propertiesLoadStatus: LoadStatus = useSelector(selectors.propertiesLoadStatus);
    const propertiesAreLoaded: boolean = useSelector(selectors.propertiesAreLoaded);
    const occupantsAreLoaded: boolean = useSelector(selectors.occupantsAreLoaded);
    const currentPermissionContext: UserPermissions | null = useSelector(selectors.currentPermissionContext);
    const propertiesWithOccupantsLoaded: number[] = useSelector(selectors.propertiesWithOccupantsLoaded);

    React.useEffect(() => {
        if (
            !disablePropertyFetch &&
            permission !== currentPermissionContext &&
            propertiesLoadStatus !== LoadStatus.PENDING
        ) {
            dispatch({
                type: Actions.LOAD_PROPERTY_TENANT_LIST,
                payload: {
                    permission,
                },
            } as PropertyTenantResolution.ActionTypes);

            (getAssociatedPropertiesOnly ? getAssociatedPropertiesByPermission : getPropertiesByPermission)(permission)
                .then((propertiesResponse: PropertyByPermissionAPI[]) => {
                    const properties: PropertyByPermission[] = propertiesResponse.map((_) => ({ ..._, occupants: [] }));
                    dispatch({
                        type: Actions.SET_PROPERTY_TENANT_LIST,
                        payload: {
                            properties,
                        },
                    } as PropertyTenantResolution.ActionTypes);
                })
                .catch(() => {
                    dispatch({
                        type: Actions.RESET_PROPERTY_TENANT_LIST,
                    } as PropertyTenantResolution.ActionTypes);
                });
        }
    }, [permission]);

    const loadOccupants = (propertyId: number) => {
        if (permission && !propertiesWithOccupantsLoaded?.includes(propertyId)) {
            dispatch({
                type: Actions.LOAD_PROPERTY_OCCUPANTS,
            } as PropertyTenantResolution.ActionTypes);

            getOccupantsByPermission(propertyId, permission).then((occupants: OccupantByPermission[]) => {
                const uniqueOccupants = getUniqueItems<OccupantByPermission>(occupants, 'id');
                dispatch({
                    type: Actions.FILL_PROPERTY_OCCUPANTS,
                    payload: {
                        propertyId,
                        occupants: uniqueOccupants,
                    },
                } as PropertyTenantResolution.ActionTypes);
            });
        }
    };

    return [propertiesAreLoaded, loadOccupants, occupantsAreLoaded];
};
